// https://git.huya.info/huyafed/components/huyafed-npm/-/tree/master/packages/taf-network
import TafNetwork from "@huyafed/taf-network/dist/taf-network.cjs.prod.js";
import { GETCOOKIE } from "@/utils";
import Constants from "@/constants";
import HUYAUserId from "@/assets/jce/HUYAUserId";
import { HUYA } from "@/assets/jce/LuckyBagServant";
import { withMiddleware } from "./middleware";

type TBase = {
  /** @see https://git.huya.info/huyafed/components/huyafed-npm/-/tree/master/packages/taf-network#bcode-%E5%AF%B9%E5%BA%94%E7%9A%84%E7%8A%B6%E6%80%81 */
  bcode: number;

  /** @see https://git.huya.info/huyafed/components/huyafed-npm/-/tree/master/packages/taf-network#sdkcode-%E5%AF%B9%E5%BA%94%E7%9A%84%E7%8A%B6%E6%80%81 */
  sdkcode: number;

  [key: string]: any;
};
type TafClassIgnoreKey = "_clone" | "_write" | "_read" | "writeTo" | "readFrom";
export type ClassType = new (...args: any) => any;

type GT<T> = T extends ClassType ? T : any;
export type TReqParams<TReqClass extends any | ClassType> = TReqClass extends ClassType
  ? Partial<Omit<InstanceType<TReqClass>, TafClassIgnoreKey>>
  : Partial<Omit<TReqClass, TafClassIgnoreKey>>;
export type TRspInfer<T extends any> = T extends ClassType ? InstanceType<T> : T;
export type TRspWrap<T> = T & TBase;
export type TTafFetch = <TRspClass extends any = any, TReqClass extends any = any>(params: {
  /** 服务端的ui, 如 accompanyui */
  moduleName: string;
  /** 接口名, 如 reactiveQuickOrderInvitation */
  funcName: string;
  ReqClass: GT<TReqClass>;
  RspClass: GT<TRspClass>;
  data: TReqParams<TReqClass>;
  /** 超时时间，默认 6 秒 */
  timeoutMS?: number;
  /** 是否使用固定ua */
  useSendWupWithForceUa?: boolean;
}) => Promise<TRspWrap<TRspInfer<TRspClass>>>;

export const getUserId = (userIdStruct: HUYAUserId.UserId = new HUYAUserId.UserId()) => {
  const result = userIdStruct;
  result.sCookie = document.cookie.replace(/\s/g, "");
  result.lUid = (GETCOOKIE("udb_uid") || 0) as number; // 传字符串也可以，防止溢出
  result.sHuYaUA = "webh5&1.0.0&huya";
  return result;
};

export const tafSdk = new TafNetwork({
  showLog: Constants.IS_DEBUG || Constants.IS_USEDEBUG,
  isDebug: Constants.IS_DEBUG,

  /** 是否为虎牙观众端app环境。需要时手动开启 */
  isApp: false,
});

// 注册事件 uri
const uris = {
  6700: HUYA.LuckyBagPanel,
};

type TUris = typeof uris;
export function listenNotice<TUri extends keyof TUris>(
  uri: TUri,
  cb: (jceStruct: TRspInfer<TUris[TUri]>) => any,
  pid?: string | number,
): Function {
  // 需要进组时，可传pid(主播uid)。
  if (Number(pid)) {
    // @ts-ignore
    tafSdk.pid = pid;
  }
  tafSdk.addListener(uri, cb);
  tafSdk.listen();
  // @ts-ignore
  return () => tafSdk.clearSingleListen(uri);
}

/**
 * 增强版 tafFetch，支持中间件
 */
export const tafFetch: TTafFetch = withMiddleware(params => {
  const {
    moduleName,
    funcName,
    ReqClass,
    RspClass,
    data,
    timeoutMS = 6000,
    useSendWupWithForceUa,
  } = params;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject("[tafFetch] timeout");
    }, timeoutMS);
    return tafSdk[
      useSendWupWithForceUa && tafSdk.sendWupWithForceUa ? "sendWupWithForceUa" : "sendWup"
    ](funcName, data, ReqClass, RspClass, moduleName)
      .then((res: any) => {
        const result = {
          bcode: res?.bcode,
          sdkcode: res?.sdkcode,
          ...(res?.data || {}),
        };
        resolve(result);
      })
      .finally(() => {
        clearTimeout(timer);
      });
  });
});

export default tafFetch;

/**
 * @see https://test-hd.huya.com/h5/jceAst/test/index.html
 * @example
 * 
// 获取极速抢单状态
// int reactiveQuickOrderInvitation(ReactiveQuickOrderInvitationReq tReq, out ReactiveQuickOrderInvitationRsp tRsp); accompanyui
export const reactiveQuickOrderInvitation = (sInvitationId: string) => {
  return tafFetch(
    {
      moduleName: "accompanyui",
      funcName: "reactiveQuickOrderInvitation",
      data: {
        tId: getUserId(),
        sInvitationId,
      },
      ReqClass: ReactiveQuickOrderInvitationJCE.ReactiveQuickOrderInvitationReq,
      RspClass: ReactiveQuickOrderInvitationJCE.ReactiveQuickOrderInvitationRsp,
    }
  );
};
 */

export const getErrorMsgFromSDK = (
  res: Awaited<ReturnType<TTafFetch>>,
  prefixErrorMsg?: string,
) => {
  const shouldShowCode = Constants.IS_DEBUG || Constants.IS_USEDEBUG;
  const codeStr = shouldShowCode ? `【${res?.bcode}】-【${res?.sdkcode}】` : "";
  if (res?.bcode !== 0 || res?.sdkcode !== 0)
    return `${prefixErrorMsg || "网络繁忙，请稍后再试"}${codeStr}`;
  return "";
};

export const checkRes = (
  res: { bcode: number; sdkcode: number; [key: string]: any },
  prefixErrorMsg?: string,
) => {
  const errorMsg = getErrorMsgFromSDK(res, prefixErrorMsg);
  if (errorMsg) throw new Error(errorMsg);
};
